'use client';

import { createPortal } from 'react-dom';
import React, { useState, useEffect } from 'react';
import { TutorialContent } from '@/lib/types/tutorial';
import { X, CheckCircle2, ShoppingCart, Wallet, Store } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface TutorialModalProps {
  content: TutorialContent | null;
  onProceed: () => void;
  onClose: () => void;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    setIsMobile(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return isMobile;
}

export default function TutorialModal({ content, onProceed, onClose }: TutorialModalProps) {
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !content) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/60 z-[60]" onClick={onClose} />

      <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4 md:p-6 pointer-events-none">
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl w-[calc(100vw-16px)] sm:max-w-lg md:max-w-4xl max-h-[calc(100dvh-16px)] sm:max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto">
          
          {isMobile ? (
            <div className="flex min-h-0 flex-col">
              
              <div className="relative shrink-0 px-4 pb-3 pt-5 sm:px-6 sm:pt-6 sm:pb-4">
                <button
                  onClick={onClose}
                  className="absolute right-3 top-3 p-2 rounded-full hover:bg-gray-100 sm:right-4 sm:top-4"
                >
                  <X className="w-4 h-4 text-gray-400 xs:w-5 xs:h-5" />
                </button>
                <h2 className="pr-10 text-base font-bold leading-tight xs:text-lg sm:text-xl">{content.title}</h2>
                <p className="text-xs text-primary-600 mt-0.5 xs:text-sm">How it works</p>
              </div>

              <div className="shrink-0 px-4 sm:px-6">
                <div className="w-full bg-black rounded-xl overflow-hidden aspect-[4/5] max-h-[35dvh] min-h-[120px]">
                  <video
                    key={content.mobileVideo}
                    src={content.mobileVideo}
                    controls
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-3 sm:px-6 sm:pt-5">
                <div className="space-y-3 pb-3 sm:space-y-4">
                  {content.steps.map((step, index) => (
                    <div key={index} className="relative flex gap-3">
                      <div className="flex flex-col items-center">
                        <CheckCircle2 className="w-5 h-5 text-primary-500 shrink-0 sm:h-6 sm:w-6" />
                        {index < content.steps.length - 1 && (
                          <div className="mt-1 w-[2px] flex-1 bg-gray-300 border-dashed border-l" />
                        )}
                      </div>
                      <p className="text-xs leading-relaxed text-gray-700 pb-3 xs:text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4">
                <Button
                  onClick={onProceed}
                  radius="full"
                  variant="default"
                  className="w-full py-4 text-sm xs:py-5 xs:text-base"
                >
                  Proceed
                </Button>
              </div>
            </div>
          ) : (
            /* DESKTOP LAYOUT - Rendered ONLY on large screens */
            <div className="flex flex-col h-full overflow-y-auto">
              <div className="relative flex flex-col items-center">
                <button onClick={onClose} className="absolute top-6 right-6 z-10 p-2 rounded-full bg-white/80 hover:bg-white">
                  <X className="w-6 h-6 text-gray-600" />
                </button>

                <div className="w-full bg-black aspect-video max-h-[50vh]">
                  <video
                    key={content.webVideo}
                    src={content.webVideo}
                    controls
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="p-8 w-full max-w-3xl">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold">{content.title}</h2>
                    <p className="text-primary-600 text-lg mt-1">How it works</p>
                  </div>
                  <div className="mt-8 space-y-4">
                    {content.steps.map((step, index) => (
                      <div key={index} className="relative">
                        <div className="flex items-start gap-3">
                          <div className="z-10 bg-white">
                            <CheckCircle2 className="w-6 h-6 text-primary-500 flex-shrink-0" />
                          </div>
                          <p className="text-sm md:text-base text-gray-700">{step}</p>
                        </div>
                        {index < content.steps.length - 1 && (
                          <div className="absolute left-3 top-6 bottom-[-24px] w-[2px] bg-gray-300 border-dashed border-l my-1" />
                        )}
                      </div>
                    ))}
                  </div>
                  <button onClick={onProceed} className="mt-8 w-full bg-primary-500 text-white py-4 rounded-full text-xl font-bold">
                    Proceed
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}


interface WelcomeVideoModalProps {
  webVideoUrl: string;
  mobileVideoUrl: string;
  onProceed: () => void;
  onClose: () => void;
  isVertical?: boolean; 
}

export function WelcomeVideoModal({
  webVideoUrl,
  mobileVideoUrl,
  onProceed,
  onClose,
  isVertical = false, 
}: WelcomeVideoModalProps) {
  const [mounted, setMounted] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;
  const videoUrl = isMobile && mobileVideoUrl ? mobileVideoUrl : webVideoUrl;

  return createPortal(
    <>
      <div 
        className="fixed inset-0 bg-white/30 z-[80] animate-in fade-in duration-500 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[90] flex items-center justify-center p-2 sm:p-4 md:p-6 pointer-events-none">
        <div className={cn(
          "w-full max-h-[calc(100dvh-16px)] flex flex-col items-center pointer-events-auto animate-in zoom-in-95 duration-300 transition-all",
          (isVertical && isMobile) ? "max-w-[min(320px,calc(100vw-16px))]" : "max-w-5xl"
        )}>
          <button 
            onClick={onClose}
            className="self-end mb-2 p-1.5 sm:p-2 text-gray-800 md:text-white/70 hover:text-black md:hover:text-white transition-colors"
          >
            <X className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          <div className={cn(
            "w-full shadow-2xl relative md:rounded-xl overflow-hidden transition-all bg-black",
            isMobile ? "max-h-[62dvh]" : "max-h-[70vh]", 
            (isVertical && isMobile) ? "aspect-[9/16]" : "aspect-video"
          )}>
            <video
              key={videoUrl}
              src={videoUrl}
              controls
              autoPlay
              preload="auto"
              playsInline
              className="w-full h-full object-contain"
            />
          </div>

          <div className={cn(
            "mt-4 md:mt-8 px-6 w-full shrink-0", 
            (isVertical && isMobile) ? "max-w-sm" : "max-w-md"
          )}>
            <Button
              onClick={onProceed}
              radius="full"
              className="w-full py-5 md:py-7 text-lg md:text-xl font-bold bg-primary-500 text-white hover:bg-primary-400 shadow-lg"
            >
              Get Started
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

interface OnboardingWelcomeModalProps {
  onClose: () => void;
  onAddPicture: () => void;
  onFundWallet: () => void;
  onCopyAddress: () => void;
  // onGoToShip: () => void;
}

const onboardingItems = [
  {
    title: "Buy for me (sourcing)",
    description: "Get a fast quote for any product with just a picture.",
    buttonLabel: "Upload Picture",
    icon: ShoppingCart,
    action: "onAddPicture",
  },
  {
    title: "Wallet (RMB)",
    description: "Add {Naira} to your wallet, get RMB. Pay your Suppliers. Pay yourself.",
    buttonLabel: "Fund Wallet",
    icon: Wallet,
    action: "onFundWallet",
  },
  {
    title: "Store (Receive Goods)",
    description: "Send Goods to China address, inspect and combine to save.",
    buttonLabel: "Copy China Address",
    icon: Store,
    action: "onCopyAddress",
  },
  // {
  //   title: "Ship",
  //   description: "Manage your shipments and packages",
  //   buttonLabel: "Go to ship",
  //   icon: Ship,
  //   action: "onGoToShip",
  // },
] as const;

export function OnboardingWelcomeModal({
  onClose,
  onAddPicture,
  onFundWallet,
  onCopyAddress,
  // onGoToShip,
}: OnboardingWelcomeModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const actions = {
    onAddPicture,
    onFundWallet,
    onCopyAddress,
    // onGoToShip,
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/20 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-[90] flex items-center justify-center p-2 pointer-events-none sm:p-4 sm:py-10">
        <div className="font-outfit relative  flex max-h-[calc(100dvh-24px)] w-[calc(100vw-16px)] max-w-[390px] flex-col overflow-hidden rounded-[22px] bg-white px-3 pb-3 pt-11 text-center pointer-events-auto xs:w-[calc(100vw-32px)] xs:px-4 xs:pb-4 sm:max-h-[calc(100vh-80px)] sm:w-[640px] sm:max-w-[640px] sm:rounded-[28px] sm:px-[42px] sm:pb-7 sm:pt-9">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close onboarding"
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#F7F7F7] text-black transition-colors hover:bg-[#ECECEC] xs:right-4 xs:top-4 sm:right-[42px] sm:top-8 sm:h-10 sm:w-10"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.7} />
          </button>

          <div className="mx-auto w-full shrink-0 px-1 sm:max-w-[490px]">
            <h2 className="text-xl font-normal leading-snug mt-4 text-[#0A0A0A] xs:text-[21px] sm:text-[22px]">
              Welcome to Proc360!
            </h2>
            <p className="mt-1.5 text-sm font-normal leading-snug text-[#666666] xs:text-base sm:mt-2 sm:text-lg">
              Buy SAFELY from China. All you need is here.
            </p>
            <p className="mx-auto mt-3 max-w-[280px] text-xs font-normal leading-relaxed text-[#484848] xs:mt-4 xs:text-sm sm:mt-5 sm:max-w-[445px] sm:text-[17px]">
              Source quality goods, receive packages, inspect, pack and ship to your doorstep.
            </p>
          </div>

          <div className="mt-3 flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-[22px] bg-[#063982] p-2 pt-3 xs:mt-4 xs:rounded-[24px] xs:p-2.5 xs:pt-4 sm:mt-5 sm:w-[556px] sm:rounded-[30px] sm:p-3 sm:pt-4">
            <h3 className="mb-2 shrink-0 text-sm font-normal leading-snug text-white xs:mb-3 xs:text-base sm:text-xl">
              Where would you like to go first?
            </h3>

            <div className="min-h-0 flex-1 pt-4 overflow-y-auto rounded-[18px] bg-white text-left sm:rounded-[24px]">
              {onboardingItems.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className={cn(
                      "flex flex-col gap-2 px-3 py-3 xs:flex-row xs:items-center xs:gap-3 xs:px-3 sm:px-4 sm:py-4",
                      index !== onboardingItems.length - 1 && "border-b border-[#EFEFEF]",
                    )}
                  >
                    <div className="flex items-center gap-3 xs:gap-3 xs:min-w-0">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E8EDF5] text-[#0E4592] xs:h-[42px] xs:w-[42px] sm:h-[52px] sm:w-[52px]">
                        <Icon className="h-5 w-5 xs:h-5 xs:w-5 sm:h-6 sm:w-6" strokeWidth={1.8} />
                      </div>
                      <div className="min-w-0 flex-1 xs:hidden">
                        <h4 className="text-sm font-normal leading-snug text-black">
                          {item.title}
                        </h4>
                        <p className="mt-0.5 text-xs font-normal leading-relaxed text-[#54545C]">
                          {item.description}
                        </p>
                      </div>
                    </div>

                    <div className="hidden min-w-0 flex-1 xs:block">
                      <h4 className="text-sm font-normal leading-snug text-black sm:text-[19px]">
                        {item.title}
                      </h4>
                      <p className="mt-0.5 text-xs font-normal leading-relaxed text-[#54545C] sm:mt-1 sm:max-w-[250px] sm:text-[15px]">
                        {item.description}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={actions[item.action]}
                      className="w-full rounded-full bg-[#0E4592] px-3 py-2.5 text-xs font-normal leading-tight text-white transition-colors hover:bg-[#0B3A7C] xs:w-auto xs:min-w-[120px] xs:shrink-0 xs:px-4 sm:h-11 sm:min-w-[160px] sm:px-5 sm:text-sm"
                    >
                      {item.buttonLabel}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
